import { State } from '@progress/kendo-data-query';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { AxiosResponse } from 'axios';
import { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import useInvoiceCategories from 'src/assets/static/view_invoice/useInvoiceCategories';
import ExportExcel, { defaultExcelOption } from 'src/components/ExportExcel';
import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import useCompany from 'src/hooks/useCompany';
import { IInvoiceListItem } from 'src/types/Invoice';
import { SetState } from 'src/types/generic';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { formatViewInvoices } from 'src/utils/transformation';

interface GridToolbarProps {
  clearFilter: () => void;
  selectionFilter: any;
  setSelectionFilter: React.Dispatch<any>;
  excelExport: (data: any[]) => void;
  rows: IInvoiceListItem[];
  invoiceDataState: any;
  dateArr: string[];
  setLoading: SetState<boolean>;
  setState: SetState<State>;
  listCompanyInvoices: (
    companyId: string,
    clientSide?: boolean
  ) => Promise<AxiosResponse<any, any>>;
  isClearFiltersDisabled: boolean;
}

const GridToolbar: FC<GridToolbarProps> = ({
  clearFilter,
  selectionFilter,
  setSelectionFilter,
  excelExport,
  rows,
  listCompanyInvoices,
  setLoading,
  setState,
  isClearFiltersDisabled,
}) => {
  const [excelOption, setExcelOption] = useState(defaultExcelOption);
  const { company } = useCompany();
  const categories = useInvoiceCategories();

  const handleExcelChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const selectedOption = e.target.value;
      setExcelOption(selectedOption);
      if (selectedOption.value === 1) {
        setLoading(true);
        excelExport(rows);
        setExcelOption(defaultExcelOption);
        setLoading(false);
      } else if (selectedOption.value === 2) {
        setLoading(true);
        listCompanyInvoices(company.companyId, true)
          .then((data) => {
            excelExport(formatViewInvoices(data.data.items));
            setExcelOption(defaultExcelOption);
            setLoading(false);
          })
          .catch((err) => {
            console.error(err);
            setLoading(false);
          });
      }
    },
    [excelExport, listCompanyInvoices, rows, setLoading, company]
  );

  return (
    <div className="flex items-center justify-between w-full">
      <ClearFilterButton onClick={clearFilter} disabled={isClearFiltersDisabled} />

      <div className="flex flex-row gap-4">
        <ExportExcel excelOption={excelOption} onExcelChanged={handleExcelChange} width={200} />
        <DropDownList
          id="view-drop-down-list"
          style={{ width: 300 }}
          data={categories}
          textField="label"
          dataItemKey="value"
          value={selectionFilter}
          onChange={(e) => {
            setSelectionFilter(e.value);
            setState((prev) => {
              if (modifyRequest(prev).filters.length) {
                toast.info('Preset filter applied. User-applied filters cleared.');
              }
              return {
                ...prev,
                filter: {
                  logic: 'and',
                  filters: [],
                },
              };
            });
          }}
        />
      </div>
    </div>
  );
};
export default GridToolbar;
